import './uploader.css';

import * as React from 'react';
import { type FileRejection } from 'react-dropzone';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { DropField } from './DropField';
import { FileUploadDetails } from './FileUploadDetails';
import { type UploadItem, type ValidationError } from './types';
import {
    buildAccept,
    formatAcceptedFileTypes,
    formatBytes,
    validateFile,
} from './utils';

export interface UploaderProps {
    /** File extensions (with or without dot) or MIME types to accept. */
    acceptedFileTypes?: string[];
    /** Maximum allowed size, in bytes. */
    maxFileSize?: number;
    /** Whether more than one file can be selected. Defaults to `false`. */
    isMultiple?: boolean;
    /**
     * When set, the drop area renders in its error variant and this
     * message replaces any internally generated validation message.
     */
    errorText?: string;
    /** Controlled list of uploads displayed below the drop area. */
    uploads?: UploadItem[];
    /** Disables both drop and browse interactions. */
    isDisabled?: boolean;
    /** Called with the files that passed validation. */
    onFilesAdded?: (files: File[]) => void;
    /** Called when the user removes a file via the X button. */
    onFileRemove?: (item: UploadItem) => void;
    /**
     * Called when the user clicks the retry button on a failed upload. The
     * retry button is only rendered when this callback is provided and the
     * item's status is `'error'`.
     */
    onFileRetry?: (item: UploadItem) => void;
    /** Called with the validation errors that caused files to be rejected. */
    onValidationError?: (errors: ValidationError[]) => void;
    /**
     * When provided, too-large / invalid-type failures are staged as a file
     * card (Figma 03/03, 03/04) instead of a drop-zone error. Drop-zone
     * errors remain for empty selection and "already have a file".
     */
    onRejectedFile?: (file: File, errorMessage: string) => void;
    className?: string;
}

const Uploader = React.forwardRef<HTMLDivElement, UploaderProps>(
    ({
        acceptedFileTypes,
        maxFileSize,
        isMultiple = false,
        errorText,
        uploads = [],
        isDisabled = false,
        onFilesAdded,
        onFileRemove,
        onFileRetry,
        onValidationError,
        onRejectedFile,
        className,
    }, ref) => {
        const { t } = useTranslation();
        const reactId = React.useId();
        const helperTextId = `${reactId}-helper`;
        const errorMessageId = `${reactId}-error`;

        const [internalError, setInternalError] = React.useState<string | undefined>();

        const accept = React.useMemo(
            () => buildAccept(acceptedFileTypes),
            [acceptedFileTypes],
        );

        const computedHelperText = React.useMemo(() => {
            const formats = formatAcceptedFileTypes(acceptedFileTypes);
            const maxSize = typeof maxFileSize === 'number' ? formatBytes(maxFileSize) : '';
            if (!formats && !maxSize) {
                return undefined;
            }
            return t('UPLOADER.SUPPORTED_FORMATS_AND_SIZE', {
                formats: formats || '*',
                maxSize: maxSize || '∞',
            });
        }, [acceptedFileTypes, maxFileSize, t]);

        const handleFilesAdded = React.useCallback(
            (files: File[]) => {
                // Figma 03/02: a second file must not replace the staged one.
                // Surface the existing single-file error via the parent.
                if (!isMultiple && uploads.length > 0) {
                    onFilesAdded?.(files);
                    return;
                }

                const errors: ValidationError[] = [];
                const accepted: File[] = [];

                for (const file of files) {
                    const error = validateFile(file, { acceptedFileTypes, maxFileSize });
                    if (error) {
                        errors.push(error);
                    } else {
                        accepted.push(file);
                    }
                }

                if (errors.length > 0) {
                    const firstError = errors[0];
                    const message = translateValidationError(firstError, { acceptedFileTypes, maxFileSize, t });
                    onValidationError?.(errors);
                    if (onRejectedFile && isCardValidationError(firstError.code)) {
                        setInternalError(undefined);
                        onRejectedFile(firstError.file, message);
                    } else {
                        setInternalError(message);
                    }
                } else {
                    setInternalError(undefined);
                }

                if (accepted.length > 0) {
                    onFilesAdded?.(accepted);
                }
            },
            [acceptedFileTypes, isMultiple, maxFileSize, onFilesAdded, onRejectedFile, onValidationError, t, uploads.length],
        );

        const handleFilesRejected = React.useCallback(
            (rejections: FileRejection[]) => {
                if (rejections.length === 0) {
                    return;
                }
                if (!isMultiple && uploads.length > 0) {
                    onFilesAdded?.(rejections.map((rejection) => rejection.file));
                    return;
                }
                const errors: ValidationError[] = rejections.map((rejection) => {
                    const first = rejection.errors[0];
                    const code = (first?.code as ValidationError['code']) ?? 'file-invalid-type';
                    return { code, file: rejection.file, message: first?.message };
                });
                const firstError = errors[0];
                const message = translateValidationError(firstError, { acceptedFileTypes, maxFileSize, t });
                onValidationError?.(errors);
                if (onRejectedFile && isCardValidationError(firstError.code)) {
                    setInternalError(undefined);
                    onRejectedFile(firstError.file, message);
                } else {
                    setInternalError(message);
                }
            },
            [acceptedFileTypes, isMultiple, maxFileSize, onFilesAdded, onRejectedFile, onValidationError, t, uploads.length],
        );

        const effectiveError = errorText ?? internalError;

        return (
            <div
                ref={ref}
                className={cn(
                    'tw-flex tw-flex-col tw-gap-6 tw-w-full',
                    'tw-items-start tw-justify-center tw-p-5 lg:!tw-p-8',
                    className,
                )}
                data-testid="uploader"
            >
                <DropField
                    accept={accept}
                    maxFileSize={maxFileSize}
                    isMultiple={isMultiple}
                    disabled={isDisabled}
                    helperText={computedHelperText}
                    errorMessage={effectiveError}
                    helperTextId={helperTextId}
                    errorMessageId={errorMessageId}
                    onFilesAdded={handleFilesAdded}
                    onFilesRejected={handleFilesRejected}
                />

                <FileUploadDetails
                    uploads={uploads}
                    isMultiple={isMultiple}
                    onRemove={onFileRemove}
                    onRetry={onFileRetry}
                />
            </div>
        );
    },
);

Uploader.displayName = 'Uploader';

function isCardValidationError(code: ValidationError['code']): boolean {
    return code === 'file-too-large' || code === 'file-invalid-type';
}

function translateValidationError(
    error: ValidationError,
    { acceptedFileTypes, maxFileSize, t }: {
        acceptedFileTypes?: string[];
        maxFileSize?: number;
        t: (key: string, values?: Record<string, string | number>) => string;
    },
): string {
    if (error.code === 'file-too-large' && typeof maxFileSize === 'number') {
        return t('UPLOADER.FILE_TOO_LARGE', { size: formatBytes(maxFileSize) });
    }
    if (error.code === 'file-invalid-type') {
        return t('UPLOADER.FILE_TYPE_INVALID', {
            types: formatAcceptedFileTypes(acceptedFileTypes) || '*',
        });
    }
    return error.message ?? t('UPLOADER.FILE_TYPE_INVALID', {
        types: formatAcceptedFileTypes(acceptedFileTypes) || '*',
    });
}

export { Uploader };
